import React, { useState, useRef, useContext } from 'react';
import AuthContext from '../../../contexts/auth';
import { useForm } from 'react-hook-form';
import { translate } from "../../../utils/Common";
import Input from '../../../components/common/Input';
import Button from '../../../components/common/Button';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import * as FiIcons from 'react-icons/fi';
import { FiX, FiLock } from 'react-icons/fi';
import styles from './FormUpdatePasswords.module.css';

const FormUpdatePasswords = ({ 
  getUserInfo, 
  setGetUserInfo, 
  userInformation, 
  validateUpdatePwd, 
  validateUpdatePwdLocker, 
  setValidateUpdatePwd, 
  setValidateUpdatePwdLocker 
}) => {
  const { handleSubmit, register, formState: { errors }, watch, reset, setError, clearErrors } = useForm();
  const userContext = useContext(AuthContext);
  const [successModal, setSuccessModal] = useState({ open: false, title: '', message: '' });

  const newPwd = useRef({});
  newPwd.current = watch("newPwd", "");
  const oldPwd = useRef({});
  oldPwd.current = watch("oldPwd", "");

  const regexLetras = /[a-zA-Z]+/;
  const regexNum = /[0-9]+/;
  const regexOnlyNum = /^[0-9]*$/;
  const regexChar = /["#$%&/()=¿?¡!_.,+*']+/;

  const [isNewPwdVisible, setIsNewPwdVisible] = useState(false);
  const [isConfirmPwdVisible, setIsConfirmPwdVisible] = useState(false);
  const [isOldPwdVisible, setIsOldPwdVisible] = useState(false);

  const validatePwdFormat = (value) => {
    if (validateUpdatePwdLocker) {
      return regexOnlyNum.test(value) && !value.includes(' ');
    } else {
      return regexLetras.test(value) && regexNum.test(value) && regexChar.test(value) && !value.includes(' ');
    }
  };

  const handleCancelUpdate = () => {
    clearErrors();
    setValidateUpdatePwd(false);
    setValidateUpdatePwdLocker(false);
  };

  const sendUpdatePwd = async (data) => {
    try {
      data.idCliente = userContext.user?.IdCliente;
      if (validateUpdatePwdLocker) {
        const result = await userContext.updatePwdLocker(data);

        switch (result) {
          case 'PWD_LOCKER_UPDATED_SUCCESSFULLY':
            setGetUserInfo(!getUserInfo);
            clearErrors();
            reset();
            setValidateUpdatePwdLocker(false);
            setSuccessModal({
              open: true,
              title: translate("dashboard.userprofile.personal.successful_update"),
              message: translate('dashboard.userprofile.updatepsw.success_message_locker'),
            });
            break;
          case 'SERVER_ERROR':
            setError('newPwd', {
              type: '',
              message: translate('dashboard.userprofile.updatepsw.server_error')
            });
            break;
          default:
            break;
        }
      } else {
        if (data.newPwd !== data.oldPwd) {
          const result = await userContext.updatePwd(data);

          switch (result) {
            case 'PASSWORD_UPDATED_SUCCESSFULLY':
              clearErrors();
              reset();
              setValidateUpdatePwd(false);
              setSuccessModal({
                open: true,
                title: translate("global.alert.ready"),
                message: translate('dashboard.userprofile.updatepsw.success_message_general'),
              });
              break;
            case 'OLD_PASSWORD_INVALID':
              setError('oldPwd', {
                type: '',
                message: translate('dashboard.userprofile.updatepsw.oldpassword_invalid')
              });
              break;
            case 'USER_NOT_FOUND':
            case 'SERVER_ERROR':
              setError('newPwd', {
                type: '',
                message: translate('dashboard.userprofile.updatepsw.server_error')
              });
              break;
            default:
              break;
          }
        } else {
          setError('newPwd', {
            type: '',
            message: translate('dashboard.userprofile.updatepsw.newpassword_match')
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(sendUpdatePwd)} className={styles.form}>
      <div className={styles.formGrid}>
        <Input
          label={translate('dashboard.userprofile.updatepsw.password')}
          type={isNewPwdVisible ? "text" : "password"}
          error={errors.newPwd?.message}
          leftIcon={<FiLock size={16} />}
          rightIcon={
            isNewPwdVisible ? <FiIcons.FiEye size={16} /> : <FiIcons.FiEyeOff size={16} />
          }
          rightIconClickable
          onRightIconClick={() => setIsNewPwdVisible(!isNewPwdVisible)}
          required
          {...register("newPwd", {
            required: {
              value: true,
              message: translate('dashboard.userprofile.updatepsw.invalid_password')
            },
            minLength: {
              value: 6,
              message: translate('dashboard.userprofile.updatepsw.minlength', { min: 6 })
            },
            maxLength: {
              value: validateUpdatePwdLocker ? 15 : 50,
              message: translate('dashboard.userprofile.updatepsw.maxlength_general', { max: validateUpdatePwdLocker ? 15 : 50 })
            },
            pattern: {
              value: validateUpdatePwdLocker ? /^[0-9]*$/ : /[A-Z]/,
              message: validateUpdatePwdLocker 
                ? translate("dashboard.userprofile.updatepsw.requirement_4") 
                : translate("dashboard.userprofile.updatepsw.has_uppercase"),
            },
            validate: value => validatePwdFormat(value) || translate('dashboard.userprofile.updatepsw.validate')
          })}
        />

        <Input
          label={translate('dashboard.userprofile.updatepsw.confirm_password')}
          type={isConfirmPwdVisible ? "text" : "password"}
          error={errors.pwdConfirm?.message}
          leftIcon={<FiLock size={16} />}
          rightIcon={
            isConfirmPwdVisible ? <FiIcons.FiEye size={16} /> : <FiIcons.FiEyeOff size={16} />
          }
          rightIconClickable
          onRightIconClick={() => setIsConfirmPwdVisible(!isConfirmPwdVisible)}
          required
          {...register("pwdConfirm", {
            required: {
              value: true,
              message: translate('dashboard.userprofile.updatepsw.invalid_password')
            },
            minLength: {
              value: 6,
              message: translate('dashboard.userprofile.updatepsw.minlength', { min: 6 })
            },
            maxLength: {
              value: validateUpdatePwdLocker ? 15 : 50,
              message: `${translate('dashboard.userprofile.updatepsw.maxlength_locker')} ${validateUpdatePwdLocker ? 15 : 50} ${translate('dashboard.userprofile.updatepsw.characters')}`
            },
            validate: value => value === newPwd.current || translate('dashboard.userprofile.updatepsw.passwords_not_match')
          })}
        />
      </div>

      {validateUpdatePwd && (
        <div className={styles.formGrid}>
          <Input
            label={translate("dashboard.userprofile.updatepsw.old_password")}
            type={isOldPwdVisible ? "text" : "password"}
            error={errors.oldPwd?.message}
            leftIcon={<FiLock size={16} />}
            rightIcon={
              isOldPwdVisible ? <FiIcons.FiEye size={16} /> : <FiIcons.FiEyeOff size={16} />
            }
            rightIconClickable
            onRightIconClick={() => setIsOldPwdVisible(!isOldPwdVisible)}
            required
            {...register("oldPwd", {
              required: {
                value: true,
                message: translate('dashboard.userprofile.updatepsw.invalid_password')
              },
              minLength: {
                value: 6,
                message: translate('dashboard.userprofile.updatepsw.minlength_oldpwd', { min: 6 })
              },
              maxLength: {
                value: 50,
                message: translate('dashboard.userprofile.updatepsw.maxlength_oldpwd', { max: 50 })
              },
            })}
          />

          <div className={styles.requirementsBox}>
            <p className={styles.requirementsTitle}>
              {translate('dashboard.userprofile.updatepsw.requirements')}
            </p>
            <ul className={styles.requirementsList}>
              <li>{translate('dashboard.userprofile.updatepsw.requirement_1')}</li>
              <li>{translate('dashboard.userprofile.updatepsw.requirement_2')}</li>
              <li>{translate('dashboard.userprofile.updatepsw.requirement_3')}</li>
              <li>{translate("dashboard.userprofile.updatepsw.requirement_5")}</li>
            </ul>
          </div>
        </div>
      )}

      {validateUpdatePwdLocker && (
        <div className={styles.requirementsBox}>
          <p className={styles.requirementsTitle}>
            {translate('dashboard.userprofile.updatepsw.requirements')}
          </p>
          <ul className={styles.requirementsList}>
            <li>{translate('dashboard.userprofile.updatepsw.requirement_1')}</li>
            <li>{translate('dashboard.userprofile.updatepsw.requirement_4')}</li>
          </ul>
        </div>
      )}

      <div className={styles.actions}>
        <Button 
          variant="outline" 
          onClick={handleCancelUpdate}
          leftIcon={<FiX size={16} />}
        >
          {translate('dashboard.userprofile.updatepsw.cancel')}
        </Button>
        <Button 
          variant="primary" 
          type="submit"
          leftIcon={<FiLock size={16} />}
        >
          {translate('dashboard.userprofile.updatepsw.update')}
        </Button>
      </div>

      <ConfirmModal
        isOpen={successModal.open}
        onClose={() => setSuccessModal((p) => ({ ...p, open: false }))}
        title={successModal.title}
        message={successModal.message}
        confirmText={translate('dashboard.userprofile.updatepsw.accept')}
        variant="primary"
        showCancelButton={false}
      />
    </form>
  );
};

export default FormUpdatePasswords;
